import {
  colors
} from "./chunk-ZJ25GT6R.mjs";
import {
  ie
} from "./chunk-W3URL2AO.mjs";
import {
  __async,
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-P26LYDRL.mjs";

// src/Alert/Root/styles.ts
var StyledRoot = ie({
  base: "atm-p-4 atm-border atm-flex atm-items-start atm-gap-2 atm-relative",
  variants: {
    variant: {
      info: "atm-bg-blue-50 atm-border-blue-200",
      warning: "atm-bg-yellow-50 atm-border-yellow-100",
      danger: "atm-bg-red-50 atm-border-red-100",
      success: "atm-bg-green-50 atm-border-green-100"
    },
    rounded: {
      true: "atm-rounded-small"
    },
    hasBorderLeft: {
      true: "atm-border-l-4"
    },
    backgroundWhite: {
      true: "!atm-bg-white-0 atm-shadow atm-border-t-0 atm-border-b-0 atm-border-r-0 "
    },
    withoutBorder: {
      true: "atm-border-t-0 atm-border-b-0 atm-border-r-0 atm-border-l-0"
    }
  },
  compoundVariants: [
    {
      hasBorderLeft: true,
      variant: "info",
      class: "atm-border-l-blue-300"
    },
    {
      hasBorderLeft: true,
      variant: "warning",
      class: "atm-border-l-yellow-300"
    },
    {
      hasBorderLeft: true,
      variant: "danger",
      class: "atm-border-l-red-300"
    },
    {
      hasBorderLeft: true,
      variant: "success",
      class: "atm-border-l-green-300"
    },
    {
      hasBorderLeft: true,
      withoutBorder: true,
      class: "atm-border-t-0 atm-border-b-0 atm-border-r-0 atm-border-l-4"
    },
    {
      hasBorderLeft: true,
      backgroundWhite: true,
      class: "atm-border-l-4"
    }
  ],
  defaultVariants: {
    variant: "info"
  }
});

// src/Alert/Root/index.tsx
import { jsx } from "react/jsx-runtime";
function Root(_a) {
  var _b = _a, {
    children,
    variant = "info",
    hasBorderLeft = false,
    backgroundWhite = false,
    withoutBorder = false,
    rounded = true
  } = _b, props = __objRest(_b, [
    "children",
    "variant",
    "hasBorderLeft",
    "backgroundWhite",
    "withoutBorder",
    "rounded"
  ]);
  return /* @__PURE__ */ jsx(
    "div",
    __spreadProps(__spreadValues({
      "data-testid": "alert-root"
    }, props), {
      className: StyledRoot({
        variant,
        hasBorderLeft,
        withoutBorder,
        rounded,
        backgroundWhite
      }),
      children
    })
  );
}
Root.displayName = "Alert.Root";

// src/Alert/Icon/index.tsx
import { AlertCircle, XCircle, CheckCircle2, AlertTriangle } from "lucide-react";

// src/Alert/Icon/styles.ts
var StyledIcon = ie({
  variants: {
    variant: {
      info: "atm-text-blue-300",
      warning: "atm-text-yellow-300 ",
      danger: "atm-text-red-300",
      success: "atm-text-green-300"
    }
  }
});

// src/Alert/Icon/index.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var ALERT_ICON_SIZE = 20;
function Icon(_a) {
  var _b = _a, { variant = "info" } = _b, props = __objRest(_b, ["variant"]);
  const renderIcon = {
    info: /* @__PURE__ */ jsx2(AlertCircle, { "data-testid": "alert-info-icon", size: ALERT_ICON_SIZE }),
    danger: /* @__PURE__ */ jsx2(XCircle, { "data-testid": "alert-danger-icon", size: ALERT_ICON_SIZE }),
    success: /* @__PURE__ */ jsx2(CheckCircle2, { "data-testid": "alert-success-icon", size: ALERT_ICON_SIZE }),
    warning: /* @__PURE__ */ jsx2(AlertTriangle, { "data-testid": "alert-warning-icon", size: ALERT_ICON_SIZE })
  };
  return /* @__PURE__ */ jsx2(
    "div",
    __spreadProps(__spreadValues({
      "data-testid": "alert-icon"
    }, props), {
      className: StyledIcon({ variant }),
      children: renderIcon[variant]
    })
  );
}
Icon.displayName = "Alert.Icon";

// src/Alert/Title/styles.ts
var StyledTitle = ie({
  base: "atm-font-semibold atm-text-base atm-leading-6 atm-text-blue-800 atm-max-w-[90%]"
});

// src/Alert/Title/index.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function Title(_a) {
  var _b = _a, { title } = _b, props = __objRest(_b, ["title"]);
  return /* @__PURE__ */ jsx3("h2", __spreadProps(__spreadValues({ "data-testid": "alert-title" }, props), { className: StyledTitle(), children: title }));
}
Title.displayName = "Alert.Title";

// src/Alert/Close/index.tsx
import { X } from "lucide-react";

// src/Alert/Close/styles.ts
var StyledClose = ie({
  base: "atm-bg-transparent atm-border-none atm-absolute atm-top-4 atm-right-4",
  variants: {
    align: {
      start: "atm-m-0",
      end: "atm-ml-auto"
    },
    relative: {
      true: "md:atm-relative md:atm-top-0 md:atm-right-0",
      false: "md:atm-top-6 md:atm-right-6"
    },
    variant: {
      info: "hover:atm-text-blue-300",
      warning: "hover:atm-text-yellow-300",
      danger: "hover:atm-text-red-300",
      success: "hover:atm-text-green-300"
    }
  }
});

// src/Alert/Close/index.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
function Close(_a) {
  var _b = _a, {
    onClose,
    relative,
    align,
    variant = "info"
  } = _b, props = __objRest(_b, [
    "onClose",
    "relative",
    "align",
    "variant"
  ]);
  return /* @__PURE__ */ jsx4(
    "button",
    __spreadProps(__spreadValues({
      "data-testid": "alert-close"
    }, props), {
      className: StyledClose({ relative, align, variant }),
      onClick: onClose,
      children: /* @__PURE__ */ jsx4(X, { size: 18 })
    })
  );
}
Close.displayName = "Alert.Close";

// src/Alert/Content/styles.ts
var StyledContent = ie({
  base: "atm-group atm-flex atm-gap-3 atm-w-full atm-flex-col",
  variants: {
    alignment: {
      horizontal: "md:atm-flex-row md:atm-items-center ",
      vertical: "atm-flex-col"
    }
  }
});

// src/Alert/Content/index.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
function Content(_a) {
  var _b = _a, {
    children,
    alignment = "vertical"
  } = _b, props = __objRest(_b, [
    "children",
    "alignment"
  ]);
  return /* @__PURE__ */ jsx5(
    "div",
    __spreadProps(__spreadValues({
      "data-testid": "alert-content"
    }, props), {
      className: StyledContent({ alignment }),
      children
    })
  );
}
Content.displayName = "Alert.Content";

// src/Alert/Description/styles.ts
var StyledDescription = ie({
  base: "atm-font-normal atm-text-sm atm-leading-5 atm-text-blue-800"
});

// src/Alert/Description/index.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function Description(_a) {
  var _b = _a, { description } = _b, props = __objRest(_b, ["description"]);
  return /* @__PURE__ */ jsx6(
    "p",
    __spreadProps(__spreadValues({
      "data-testid": "alert-description"
    }, props), {
      className: StyledDescription(),
      children: description
    })
  );
}
Description.displayName = "Alert.Description";

// src/Alert/Button/styles.ts
var StyledButton = ie({
  base: "atm-text-white-0 atm-px-4 atm-py-2 atm-rounded-small atm-font-medium atm-text-sm atm-leading-4 atm-w-fit atm-transition",
  variants: {
    align: {
      start: "atm-m-0",
      end: "atm-ml-auto"
    },
    variant: {
      info: "atm-bg-blue-300 hover:atm-bg-blue-400",
      warning: "atm-bg-yellow-300 hover:atm-bg-yellow-500",
      danger: "atm-bg-red-300 hover:atm-bg-red-500",
      success: "atm-bg-green-300 hover:atm-bg-green-700"
    }
  }
});

// src/Alert/Button/index.tsx
import { Loader2 } from "lucide-react";
import { Fragment, jsx as jsx7 } from "react/jsx-runtime";
function Button(_a) {
  var _b = _a, {
    text,
    align = "start",
    variant = "info",
    onClick,
    children,
    isLoading = false
  } = _b, props = __objRest(_b, [
    "text",
    "align",
    "variant",
    "onClick",
    "children",
    "isLoading"
  ]);
  const content = text != null ? text : children;
  return /* @__PURE__ */ jsx7(
    "button",
    __spreadProps(__spreadValues({
      "data-testid": "alert-button"
    }, props), {
      className: StyledButton({ align, variant }),
      onClick,
      children: isLoading ? /* @__PURE__ */ jsx7(Loader2, { className: "atm-h-4 atm-w-4 atm-animate-spinSlow" }) : /* @__PURE__ */ jsx7(Fragment, { children: content })
    })
  );
}
Button.displayName = "Alert.Button";

// src/Alert/index.tsx
var Alert = {
  Root,
  Icon,
  Title,
  Close,
  Content,
  Description,
  Button
};

// src/Button/index.tsx
import { Slot, Slottable } from "@radix-ui/react-slot";

// src/Button/styles.ts
var StyledButton2 = ie({
  base: "atm-flex atm-justify-center atm-items-center atm-h-fit atm-gap-1 atm-px-4 atm-py-2 atm-border atm-font-bold atm-text-base atm-font-sans atm-leading-6 atm-rounded atm-transition atm-cursor-pointer disabled:atm-cursor-not-allowed",
  variants: {
    variant: {
      primary: "atm-bg-blue-300 atm-border-blue-300 atm-text-white-0 hover:atm-bg-blue-400 hover:atm-border-blue-400 disabled:atm-opacity-70  disabled:atm-border-opacity-70 disabled:hover:atm-bg-blue-300 disabled:hover:atm-border-blue-300",
      secondary: "atm-bg-white-0 atm-border-blue-300 atm-text-blue-300 hover:atm-bg-blue-50 disabled:atm-opacity-70 disabled:atm-bg-blue-50",
      outline: "atm-bg-white-0 atm-border-gray-200 atm-text-gray-700 hover:atm-bg-gray-100 disabled:atm-bg-gray-100 disabled:atm-border-gray-150 disabled:atm-text-gray-300",
      text: "atm-bg-transparent atm-border-transparent atm-text-blue-300 disabled:atm-text-gray-300 hover:disabled:atm-bg-gray-100 hover:atm-bg-blue-50 ",
      ghost: "atm-bg-transparent atm-border-transparent atm-text-gray-600 disabled:atm-text-gray-300 hover:disabled:atm-bg-gray-100 hover:atm-bg-gray-100 "
    },
    fullWidth: {
      true: "atm-w-full"
    },
    asIcon: {
      true: "!atm-p-1"
    }
  }
});

// src/Button/index.tsx
import { forwardRef, useImperativeHandle, useRef } from "react";
import { Loader2 as Loader22 } from "lucide-react";
import { jsx as jsx8 } from "react/jsx-runtime";
var Button2 = forwardRef(
  (_a, ref) => {
    var _b = _a, {
      asChild = false,
      variant = "primary",
      fullWidth = false,
      className,
      asIcon,
      isLoading,
      spinnerSize = 18
    } = _b, props = __objRest(_b, [
      "asChild",
      "variant",
      "fullWidth",
      "className",
      "asIcon",
      "isLoading",
      "spinnerSize"
    ]);
    const Component = asChild ? Slot : "button";
    const componentRef = useRef(null);
    useImperativeHandle(ref, () => componentRef.current);
    return /* @__PURE__ */ jsx8(
      Component,
      __spreadProps(__spreadValues({
        "data-testid": "button-component"
      }, props), {
        ref: componentRef,
        className: StyledButton2({ variant, asIcon, fullWidth, className }),
        children: isLoading && !asChild ? /* @__PURE__ */ jsx8(
          Loader22,
          {
            "data-testid": "spinner",
            size: spinnerSize,
            className: "atm-animate-spinSlow"
          }
        ) : /* @__PURE__ */ jsx8(Slottable, { children: props.children })
      })
    );
  }
);
Button2.displayName = "Button";

// src/Checkbox/index.tsx
import * as RadixCheckbox from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import { forwardRef as forwardRef2 } from "react";

// src/Checkbox/styles.ts
var StyledContainer = ie({
  base: "group atm-w-fit atm-grid atm-grid-flow-col-dense atm-justify-start atm-gap-3",
  variants: {
    align: {
      top: "atm-items-start",
      center: "atm-items-center",
      bottom: "atm-items-end"
    }
  }
});
var StyledLabel = ie({
  base: "atm-cursor-pointer data-[disabled=true]:atm-cursor-not-allowed",
  variants: {
    size: {
      small: "atm-text-xs",
      medium: "atm-text-sm",
      large: "atm-text-base"
    }
  }
});
var StyledRoot2 = ie({
  base: "atm-rounded-small atm-overflow-hidden atm-bg-white-0 atm-border atm-border-gray-300 atm-transition disabled:atm-bg-gray-100 disabled:atm-cursor-not-allowed focus:atm-outline focus:atm-outline-default-100 focus:atm-border-default-400 data-[state=checked]:atm-border-none ",
  variants: {
    size: {
      small: "atm-h-[18px] atm-w-[18px]",
      medium: "atm-h-[20px] atm-w-[20px]",
      large: "atm-h-[22px] atm-w-[22px]"
    }
  }
});
var StyledIndicator = ie({
  base: "atm-h-full atm-w-full atm-flex atm-items-center atm-justify-center atm-text-white-0 atm-bg-default-400 data-[disabled]:atm-bg-gray-300"
});
var StyledIcon2 = ie({
  variants: {
    size: {
      small: "atm-h-[12px] atm-w-[12px]",
      medium: "atm-h-[14px] atm-w-[14px]",
      large: "atm-h-[16px] atm-w-[16px]"
    }
  }
});

// src/Checkbox/index.tsx
import { jsx as jsx9, jsxs } from "react/jsx-runtime";
var Checkbox = forwardRef2(
  (_a, ref) => {
    var _b = _a, {
      id,
      label,
      size = "medium",
      align = "top",
      className
    } = _b, props = __objRest(_b, [
      "id",
      "label",
      "size",
      "align",
      "className"
    ]);
    return /* @__PURE__ */ jsxs(
      "div",
      {
        "data-testid": "checkbox-container",
        className: StyledContainer({ align, className }),
        children: [
          /* @__PURE__ */ jsx9(
            RadixCheckbox.Root,
            __spreadProps(__spreadValues({
              ref,
              id,
              "data-testid": "checkbox-root"
            }, props), {
              className: StyledRoot2({ size }),
              children: /* @__PURE__ */ jsx9(
                RadixCheckbox.Indicator,
                {
                  "data-testid": "checkbox-indicator",
                  className: StyledIndicator(),
                  children: /* @__PURE__ */ jsx9(
                    Check,
                    {
                      "data-testid": "checkbox-icon",
                      className: StyledIcon2({ size })
                    }
                  )
                }
              )
            })
          ),
          label && /* @__PURE__ */ jsx9(
            "label",
            {
              htmlFor: id,
              "data-testid": "checkbox-label",
              "data-disabled": props.disabled,
              className: StyledLabel({ size }),
              children: label
            }
          )
        ]
      }
    );
  }
);
Checkbox.displayName = "Checkbox";

// src/Input/index.tsx
import { Loader2 as Loader23 } from "lucide-react";
import { forwardRef as forwardRef3 } from "react";

// src/Input/styles.ts
var StyledLabel2 = ie({
  base: "atm-text-sm atm-font-sans atm-font-medium  atm-text-gray-600 atm-mb-1 atm-flex atm-gap-1.5"
});
var StyledLabelOptional = ie({
  base: "atm-text-sm atm-text-gray-300 atm-font-normal atm-font-sans"
});
var StyledInput = ie({
  base: "atm-flex atm-h-11 atm-w-full atm-px-4 atm-py-2 atm-font-sans atm-text-gray-600 atm-text-sm  atm-font-normal atm-transition-colors placeholder:atm-text-gray-300 focus-visible:atm-outline-none disabled:atm-cursor-not-allowed disabled:atm-bg-gray-50 disabled:atm-text-gray-300 disabled:atm-opacity-100 disabled:atm-border-gray-300",
  variants: {
    variant: {
      default: "atm-border atm-border-gray-200 atm-rounded-lg focus-visible:atm-border-blue-300 focus-visible:atm-ring-1 atm-ring-blue-50"
    },
    error: {
      true: "atm-border-red-300 focus-visible:atm-ring-red-100 focus-visible:atm-border-red-300"
    },
    icon: {
      true: "atm-pr-10"
    },
    startIcon: {
      true: "atm-pl-10"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
var StyledErrorMessage = ie({
  base: "atm-text-sm atm-font-sans atm-text-red-300 atm-block atm-font-normal",
  variants: {
    limitText: {
      true: "atm-truncate atm-w-[85%]"
    }
  }
});
var StyledInputIcon = ie({
  base: "atm-h-4 atm-w-4 atm-absolute atm-top-3.5 atm-right-4 atm-text-gray-400",
  variants: {
    error: {
      true: "atm-text-red-300"
    },
    loading: {
      true: "atm-animate-spinSlow"
    },
    onClickIcon: {
      true: "atm-cursor-pointer atm-transition-colors hover:atm-text-gray-500"
    }
  }
});
var StyledInputStartIcon = ie({
  base: "atm-h-4 atm-w-4 atm-absolute atm-top-3.5 atm-left-4 atm-text-gray-400",
  variants: {
    error: {
      true: "atm-text-red-300"
    }
  }
});

// src/Input/index.tsx
import { jsx as jsx10, jsxs as jsxs2 } from "react/jsx-runtime";
var Input = forwardRef3(
  (_a, ref) => {
    var _b = _a, {
      className,
      name,
      label,
      error,
      variant,
      required = true,
      icon: Icon2,
      startIcon: StartIcon,
      loading,
      onClickIcon,
      addonBelow
    } = _b, props = __objRest(_b, [
      "className",
      "name",
      "label",
      "error",
      "variant",
      "required",
      "icon",
      "startIcon",
      "loading",
      "onClickIcon",
      "addonBelow"
    ]);
    const isInvalidInput = Boolean(error);
    const hasIcon = Boolean(Icon2);
    const haStartIcon = Boolean(StartIcon);
    return /* @__PURE__ */ jsxs2("div", { children: [
      Boolean(label) && /* @__PURE__ */ jsxs2(
        "label",
        {
          "data-testid": "input-label",
          htmlFor: name,
          className: StyledLabel2(),
          children: [
            label,
            !required && /* @__PURE__ */ jsx10(
              "span",
              {
                className: StyledLabelOptional(),
                "data-testid": "input-optional-message",
                children: "(Opcional)"
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsxs2("div", { className: "atm-relative", children: [
        StartIcon && /* @__PURE__ */ jsx10(
          StartIcon,
          {
            "data-testid": "input-start-icon",
            className: StyledInputStartIcon({ error: isInvalidInput })
          }
        ),
        /* @__PURE__ */ jsx10(
          "input",
          __spreadValues({
            name,
            ref,
            className: StyledInput({
              variant,
              className,
              error: isInvalidInput,
              icon: hasIcon,
              startIcon: haStartIcon
            }),
            "data-testid": "input-element"
          }, props)
        ),
        !loading && Icon2 && /* @__PURE__ */ jsx10(
          Icon2,
          {
            "data-testid": "input-end-icon",
            onClick: onClickIcon,
            className: StyledInputIcon({
              error: isInvalidInput,
              loading,
              onClickIcon: Boolean(onClickIcon)
            })
          }
        ),
        loading && /* @__PURE__ */ jsx10(
          Loader23,
          {
            "data-testid": "input-loading",
            className: StyledInputIcon({ error: isInvalidInput, loading })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs2("div", { className: "atm-flex atm-my-1 atm-items-center", children: [
        isInvalidInput && /* @__PURE__ */ jsx10(
          "p",
          {
            className: StyledErrorMessage({
              limitText: Boolean(addonBelow)
            }),
            "data-testid": "input-error-message",
            children: error
          }
        ),
        addonBelow
      ] })
    ] });
  }
);

// src/DropdownMenu/Content/index.tsx
import { forwardRef as forwardRef4 } from "react";
import {
  Content as ContentRadix
} from "@radix-ui/react-dropdown-menu";

// src/DropdownMenu/Content/styles.ts
var styledContent = ie({
  base: "atm-bg-white-0 atm-min-w-[220px] atm-rounded-md atm-will-change-[opacity,transform] data-[side=top]:atm-animate-slideDownAndFade data-[side=right]:atm-animate-slideLeftAndFade data-[side=bottom]:atm-animate-slideUpAndFade data-[side=left]:atm-animate-slideRightAndFade atm-shadow-[rgba(13,_38,_76,_0.14)_0px_4px_12px]"
});

// src/DropdownMenu/Content/index.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
var Content2 = forwardRef4(
  (_a, ref) => {
    var _b = _a, { children, sideOffset, className } = _b, props = __objRest(_b, ["children", "sideOffset", "className"]);
    return /* @__PURE__ */ jsx11(
      ContentRadix,
      __spreadProps(__spreadValues({
        sideOffset,
        className: styledContent({ className })
      }, props), {
        ref,
        children
      })
    );
  }
);
Content2.displayName = "DropdownMenu.Content";

// src/DropdownMenu/Item/index.tsx
import { forwardRef as forwardRef5 } from "react";
import {
  Item as ItemRadix
} from "@radix-ui/react-dropdown-menu";

// src/DropdownMenu/Item/styles.ts
var StyledItem = ie({
  base: "atm-py-2 atm-px-4 disabled:atm-opacity-0 first:atm-rounded-t-md last:atm-rounded-b-md atm-cursor-pointer hover:atm-bg-gray-100 focus-visible:atm-bg-gray-100 atm-outline-none atm-transition-colors aria-disabled:atm-cursor-not-allowed  aria-disabled:atm-text-gray-300"
});

// src/DropdownMenu/Item/index.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
var Item = forwardRef5(
  (_a, ref) => {
    var _b = _a, { children, className } = _b, props = __objRest(_b, ["children", "className"]);
    return /* @__PURE__ */ jsx12(ItemRadix, __spreadProps(__spreadValues({}, props), { className: StyledItem({ className }), ref, children }));
  }
);
Item.displayName = "DropdownMenu.Item";

// src/DropdownMenu/Trigger/index.tsx
import {
  Trigger as TriggerRadix
} from "@radix-ui/react-dropdown-menu";
import { forwardRef as forwardRef6 } from "react";
import { jsx as jsx13 } from "react/jsx-runtime";
var Trigger = forwardRef6(
  (_a, ref) => {
    var _b = _a, { children } = _b, props = __objRest(_b, ["children"]);
    return /* @__PURE__ */ jsx13(TriggerRadix, __spreadProps(__spreadValues({}, props), { ref, children }));
  }
);
Trigger.displayName = "DropdownMenu.Trigger";

// src/DropdownMenu/Root/index.tsx
import {
  Root as RootRadix
} from "@radix-ui/react-dropdown-menu";
import { jsx as jsx14 } from "react/jsx-runtime";
var Root3 = (_a) => {
  var _b = _a, { children } = _b, props = __objRest(_b, ["children"]);
  return /* @__PURE__ */ jsx14(RootRadix, __spreadProps(__spreadValues({}, props), { children }));
};
Root3.displayName = "DropdownMenu.Root";

// src/DropdownMenu/Portal/index.tsx
import {
  Portal as RadixPortal
} from "@radix-ui/react-dropdown-menu";
import { jsx as jsx15 } from "react/jsx-runtime";
var Portal = (_a) => {
  var _b = _a, { children } = _b, props = __objRest(_b, ["children"]);
  return /* @__PURE__ */ jsx15(RadixPortal, __spreadProps(__spreadValues({}, props), { children }));
};
Portal.displayName = "DropdownMenu.Portal";

// src/DropdownMenu/index.tsx
var DropdownMenu = {
  Content: Content2,
  Item,
  Trigger,
  Root: Root3,
  Portal
};

// src/Radio/Root/index.tsx
import * as RadioGroup from "@radix-ui/react-radio-group";

// src/Radio/context/RadioContext.tsx
import {
  createContext,
  useContext,
  useMemo,
  useState
} from "react";
import { jsx as jsx16 } from "react/jsx-runtime";
var RadioContext = createContext({});
function RadioProvider({ children }) {
  const [error, setError] = useState(false);
  const [size, setSize] = useState("medium");
  const [iconType, setIconType] = useState("default");
  const [disabled, setDisabled] = useState(false);
  const contextValue = useMemo(
    () => ({
      error,
      setError,
      size,
      setSize,
      iconType,
      setIconType,
      disabled,
      setDisabled
    }),
    [
      error,
      setError,
      size,
      setSize,
      iconType,
      setIconType,
      disabled,
      setDisabled
    ]
  );
  return /* @__PURE__ */ jsx16(RadioContext.Provider, { value: contextValue, children });
}
var useRadioContext = () => {
  const context = useContext(RadioContext);
  return context;
};

// src/Radio/Root/index.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
var Root5 = ({ children }) => {
  return /* @__PURE__ */ jsx17(RadioGroup.Root, { children: /* @__PURE__ */ jsx17(RadioProvider, { children }) });
};
Root5.displayName = "Root";

// src/Radio/Group/index.tsx
import * as RadioGroup2 from "@radix-ui/react-radio-group";
import { forwardRef as forwardRef7 } from "react";

// src/Radio/Group/styles.ts
var Styles = ie({
  slots: {
    container: "atm-flex atm-flex-wrap atm-gap-8"
  },
  variants: {
    vertical: {
      true: {
        container: "atm-flex-col"
      }
    }
  }
});

// src/Radio/Group/index.tsx
import { jsx as jsx18 } from "react/jsx-runtime";
var Group = forwardRef7(
  (_a, ref) => {
    var _b = _a, {
      children,
      error,
      size = "medium",
      iconType = "default",
      disabled,
      vertical
    } = _b, rest = __objRest(_b, [
      "children",
      "error",
      "size",
      "iconType",
      "disabled",
      "vertical"
    ]);
    const { setDisabled, setSize, setError, setIconType } = useRadioContext();
    setDisabled(Boolean(disabled));
    setError(Boolean(error));
    setSize(size);
    setIconType(iconType);
    const S = Styles({ vertical });
    return /* @__PURE__ */ jsx18(
      RadioGroup2.Root,
      __spreadProps(__spreadValues({}, rest), {
        ref,
        className: S.container(),
        "data-testid": "radio-group-component",
        children
      })
    );
  }
);
Group.displayName = "Group";

// src/Radio/Item/index.tsx
import * as RadioGroup3 from "@radix-ui/react-radio-group";
import { Check as Check2 } from "lucide-react";

// src/Radio/Item/styles.ts
var Styles2 = ie({
  slots: {
    container: "atm-flex atm-items-center atm-gap-3",
    itemWrapper: "atm-flex",
    item: "atm-border-solid atm-border-[1px] atm-border-gray-150 data-[state=checked]:atm-border-default-400 hover:atm-border-default-400 focus:atm-outline focus:atm-outline-default-100 focus:atm-outline-4 focus:atm-border-default-400 atm-rounded-full atm-bg-white-0 hover:atm-bg-default-100 atm-transition",
    indicator: "atm-flex atm-h-full atm-w-full atm-items-center atm-justify-center atm-rounded-full atm-bg-default-400",
    icon: "atm-text-white-0",
    label: "atm-text-gray-600 atm-font-sans"
  },
  variants: {
    iconType: {
      default: {
        icon: "atm-rounded-full atm-bg-white-0"
      },
      success: {
        icon: ""
      }
    },
    size: {
      small: {
        label: "atm-text-xs",
        item: "atm-h-[18px] atm-w-[18px]",
        icon: "atm-h-[10px] atm-w-[10px]"
      },
      medium: {
        label: "atm-text-sm",
        item: "atm-h-[20px] atm-w-[20px]",
        icon: "atm-h-[12px] atm-w-[12px]"
      },
      large: {
        label: "atm-text-base",
        item: "atm-h-[22px] atm-w-[22px]",
        icon: "atm-h-[14px] atm-w-[14px]"
      }
    },
    error: {
      true: {
        item: "data-[state=unchecked]:atm-border-red-400 focus:data-[state=unchecked]:atm-outline-red-100 hover:atm-bg-red-50"
      }
    },
    disabled: {
      true: {
        item: "disabled:atm-border-gray-150 disabled:atm-bg-gray-50 disabled:atm-cursor-not-allowed data-[state=checked]:atm-bg-opacity-10 data-[state=checked]:atm-border-opacity-10",
        label: "atm-text-gray-150 atm-cursor-not-allowed ",
        indicator: "atm-opacity-30"
      }
    }
  }
});

// src/Radio/Item/index.tsx
import { jsx as jsx19, jsxs as jsxs3 } from "react/jsx-runtime";
var Item3 = (_a) => {
  var _b = _a, {
    children,
    disabled: isSomeDisabled,
    value,
    name
  } = _b, rest = __objRest(_b, [
    "children",
    "disabled",
    "value",
    "name"
  ]);
  const Icon2 = (props) => iconType === "success" ? /* @__PURE__ */ jsx19(Check2, __spreadProps(__spreadValues({ "data-testid": "radio-item-svg-success-icon" }, props), { size: 10 })) : /* @__PURE__ */ jsx19("div", __spreadValues({ "data-testid": "radio-item-default-icon" }, props));
  const { error, disabled: isAllDisabled, iconType, size } = useRadioContext();
  const disabled = isAllDisabled || isSomeDisabled;
  const isError = !disabled && error;
  const S = Styles2({ size, iconType, error: isError, disabled });
  return /* @__PURE__ */ jsxs3("div", { "data-testid": "radio-item-component", className: S.container(), children: [
    /* @__PURE__ */ jsx19("div", { className: S.itemWrapper(), children: /* @__PURE__ */ jsx19(
      RadioGroup3.Item,
      __spreadProps(__spreadValues({
        id: name,
        value,
        className: S.item(),
        disabled
      }, rest), {
        children: /* @__PURE__ */ jsx19(RadioGroup3.Indicator, { className: S.indicator(), children: /* @__PURE__ */ jsx19(Icon2, { className: S.icon() }) })
      })
    ) }),
    children && /* @__PURE__ */ jsx19("label", { htmlFor: name, className: S.label(), children })
  ] });
};

// src/Radio/index.tsx
var Radio = {
  Root: Root5,
  Group,
  Item: Item3
};

// src/InputMask/InputMoney/mask.ts
var applyInputMask = (value) => formatMoney(value);
function formatInputValue(value) {
  const parsedValue = formatDecimals(value);
  return formatDecimals(applyInputMask(parsedValue));
}
function formatMoney(value) {
  var _a;
  const onlyDigits = (_a = value == null ? void 0 : value.toString()) == null ? void 0 : _a.replace(/\D+/g, "");
  const numberValue = Number.parseInt(onlyDigits || "0", 10);
  return Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2
  }).format(numberValue / 100);
}
var formatDecimals = (value) => value.replace(/(?![,])\D/g, "").replace(/\s/g, "").replace(/,/g, ".");

// src/InputMask/InputMoney/index.tsx
import {
  forwardRef as forwardRef8,
  useImperativeHandle as useImperativeHandle2,
  useLayoutEffect,
  useRef as useRef2
} from "react";
import { jsx as jsx20 } from "react/jsx-runtime";
var InputMoney = forwardRef8(
  (_a, ref) => {
    var props = __objRest(_a, []);
    const inputRef = useRef2(null);
    useImperativeHandle2(ref, () => inputRef.current);
    function applyMaskToDefaultValue() {
      if (inputRef.current) {
        inputRef.current.value = applyInputMask(
          props.value || props.defaultValue
        );
      }
    }
    useLayoutEffect(() => {
      applyMaskToDefaultValue();
    }, []);
    function handleChange(event) {
      var _a2;
      const initialValue = `${event.target.value}`;
      event.target.value = applyInputMask(event.target.value);
      (_a2 = props == null ? void 0 : props.onChange) == null ? void 0 : _a2.call(props, event, formatInputValue(initialValue));
    }
    return /* @__PURE__ */ jsx20(Input, __spreadProps(__spreadValues({}, props), { ref: inputRef, onChange: handleChange, type: "text" }));
  }
);

// src/InputMask/InputPercentage/index.tsx
import {
  forwardRef as forwardRef9,
  useImperativeHandle as useImperativeHandle3,
  useLayoutEffect as useLayoutEffect2,
  useRef as useRef3
} from "react";

// src/InputMask/InputPercentage/constants/index.ts
var PERCENTAGE_CONFIG = {
  mask: false,
  decimalDigits: 2,
  min: 0,
  max: 100
};

// src/InputMask/InputPercentage/mask.ts
var formatPercentage = (value = "", config) => {
  if (!value)
    return "";
  const configs = __spreadValues(__spreadValues({}, PERCENTAGE_CONFIG), config);
  const formattedValue = String(value).replace(/\,/, ".").replace("%", "");
  if (Number.parseFloat(formattedValue) > configs.max)
    return configs.max;
  if (Number.parseFloat(formattedValue) < configs.min)
    return configs.min;
  return String(formattedValue).replace(/(?![.])(\D)/g, "").replace(/(\d*\.\d*)(.*)/, "$1").replace(new RegExp(`(\\d*\\.\\d{${configs.decimalDigits}})(.*)`), "$1");
};

// src/InputMask/InputPercentage/index.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var InputPercentage = forwardRef9((_a, ref) => {
  var _b = _a, { min = 0, max = 100, decimalDigits = 2 } = _b, props = __objRest(_b, ["min", "max", "decimalDigits"]);
  const inputRef = useRef3(null);
  useImperativeHandle3(ref, () => inputRef.current);
  function applyMaskToDefaultValue() {
    if (inputRef.current) {
      inputRef.current.value = formatPercentage(
        props.value || props.defaultValue,
        { min, max, decimalDigits }
      );
    }
  }
  useLayoutEffect2(() => {
    applyMaskToDefaultValue();
  }, []);
  function handleChange(event) {
    var _a2;
    event.target.value = formatPercentage(event.target.value, {
      min,
      max,
      decimalDigits
    });
    (_a2 = props == null ? void 0 : props.onChange) == null ? void 0 : _a2.call(props, event);
  }
  return /* @__PURE__ */ jsx21(Input, __spreadProps(__spreadValues({}, props), { ref: inputRef, onChange: handleChange }));
});

// src/InputMask/MaskedInput/index.tsx
import {
  forwardRef as forwardRef10,
  useCallback,
  useImperativeHandle as useImperativeHandle4,
  useLayoutEffect as useLayoutEffect3,
  useRef as useRef4
} from "react";

// src/InputMask/MaskedInput/mask.ts
function applyInputMask2(value, mask) {
  if (!value)
    return "";
  let formattedValue = "";
  const valueWithoutSpecialCharacters = value.replace(/\D/g, "");
  let valueIndex = 0;
  let maskIndex = 0;
  while (maskIndex < mask.length && valueIndex < valueWithoutSpecialCharacters.length) {
    if (!Number.isNaN(Number(mask[maskIndex])) && mask[maskIndex] !== " ") {
      formattedValue += valueWithoutSpecialCharacters[valueIndex];
      valueIndex++;
    } else {
      formattedValue += mask[maskIndex];
    }
    maskIndex++;
  }
  return formattedValue;
}

// src/InputMask/MaskedInput/index.tsx
import { jsx as jsx22 } from "react/jsx-runtime";
var MaskedInput = forwardRef10(
  (_a, ref) => {
    var _b = _a, { mask } = _b, props = __objRest(_b, ["mask"]);
    const inputRef = useRef4(null);
    useImperativeHandle4(ref, () => inputRef.current);
    const applyMaskToDefaultValue = useCallback(() => {
      if (inputRef.current) {
        inputRef.current.value = applyInputMask2(
          props.value || props.defaultValue,
          mask
        );
      }
    }, [mask, props.value, props.defaultValue]);
    useLayoutEffect3(() => {
      applyMaskToDefaultValue();
    }, [applyMaskToDefaultValue]);
    function handleInputChange(event) {
      var _a2;
      const initialValue = String(event.target.value);
      event.target.value = applyInputMask2(event.target.value, mask);
      (_a2 = props == null ? void 0 : props.onChange) == null ? void 0 : _a2.call(props, event, initialValue);
    }
    return /* @__PURE__ */ jsx22(Input, __spreadProps(__spreadValues({}, props), { ref: inputRef, onChange: handleInputChange }));
  }
);

// src/InputMask/index.tsx
var InputMask = {
  Money: InputMoney,
  Percentage: InputPercentage,
  Masked: MaskedInput
};

// src/Clipboard/index.tsx
import { useState as useState2, forwardRef as forwardRef11 } from "react";

// src/Clipboard/styles.ts
var StyledClipboard = ie({
  slots: {
    container: "atm-flex",
    block: "atm-transition-all atm-ease-in-out atm-flex atm-justify-between atm-items-center atm-h-11 atm-w-full atm-border atm-border-gray-150 atm-rounded-l-lg",
    text: "atm-uppercase atm-font-bold atm-text-gray-400 atm-px-4 atm-py-2 atm-truncate",
    actionBlock: "atm-transition-all atm-ease-in-out atm-cursor-pointer atm-flex atm-items-center atm-justify-center atm-p-[10px] atm-h-full atm-rounded-r-lg",
    icon: ""
  },
  variants: {
    isCopied: {
      false: {
        actionBlock: "atm-bg-default-400 atm-outline-default-400",
        icon: "atm-text-white-0"
      },
      true: {
        actionBlock: "atm-bg-gray-150 atm-outline-gray-150",
        icon: "atm-stroke-gray-400"
      }
    }
  }
});

// src/Clipboard/index.tsx
import { Copy, Check as Check3 } from "lucide-react";
import { jsx as jsx23, jsxs as jsxs4 } from "react/jsx-runtime";
var Clipboard = forwardRef11(
  (_a, ref) => {
    var _b = _a, { text, onSuccess, className } = _b, props = __objRest(_b, ["text", "onSuccess", "className"]);
    const [isCopied, setIsCopied] = useState2(false);
    const S = StyledClipboard({ isCopied, className });
    const handleCopyToClipboard = () => __async(void 0, null, function* () {
      yield navigator.clipboard.writeText(text).then(() => {
        setIsCopied(true);
        onSuccess && onSuccess();
      });
    });
    const Icon2 = () => isCopied ? /* @__PURE__ */ jsx23(Check3, { "data-testid": "clipboard-svg-check", className: S.icon() }) : /* @__PURE__ */ jsx23(Copy, { "data-testid": "clipboard-svg-copy", className: S.icon() });
    return /* @__PURE__ */ jsxs4("div", { "data-testid": "clipboard-component", className: S.container(), children: [
      /* @__PURE__ */ jsx23(
        "div",
        __spreadProps(__spreadValues({
          "data-testid": "clipboard-block",
          className: S.block(),
          ref
        }, props), {
          children: /* @__PURE__ */ jsx23("span", { className: S.text(), children: text })
        })
      ),
      /* @__PURE__ */ jsx23("button", { onClick: handleCopyToClipboard, className: S.actionBlock(), children: /* @__PURE__ */ jsx23(Icon2, {}) })
    ] });
  }
);
Clipboard.displayName = "Clipboard";

// src/Badge/styles.ts
var StyledBadge = ie({
  base: "atm-text-[13px] atm-w-fit atm-h-[22px] atm-font-sans atm-rounded-2xl atm-px-2 atm-font-medium atm-leading-4 atm-flex atm-items-center",
  variants: {
    color: {
      blue: "before:atm-bg-blue-300 atm-bg-blue-50 atm-text-blue-300",
      "dark-blue": "before:atm-bg-blue-700 atm-bg-blue-50 atm-text-blue-700",
      green: "before:atm-bg-green-300 atm-bg-green-50 atm-text-green-300",
      orange: "before:atm-bg-orange-300 atm-bg-yellow-50 atm-text-orange-300",
      red: "before:atm-bg-red-300 atm-bg-red-50 atm-text-red-300",
      gray: "before:atm-bg-gray-400 atm-bg-gray-100 atm-text-gray-400",
      yellow: "before:atm-bg-yellow-600 atm-bg-yellow-50 atm-text-yellow-600",
      purple: "before:atm-bg-purple-600 atm-bg-purple-50 atm-text-purple-600"
    },
    variant: {
      filled: "",
      ghost: "!atm-bg-transparent !atm-text-gray-600"
    },
    showDot: {
      true: "before:atm-block before:atm-rounded-full before:atm-w-[6px] before:atm-h-[6px] atm-gap-[6px]",
      false: ""
    }
  },
  compoundVariants: [
    {
      color: "blue",
      variant: "ghost",
      class: "before:!atm-bg-blue-300"
    },
    {
      color: "dark-blue",
      variant: "ghost",
      class: "before:!atm-bg-blue-700"
    },
    {
      color: "orange",
      variant: "ghost",
      class: "before:!atm-bg-orange-300"
    },
    {
      color: "green",
      variant: "ghost",
      class: "before:!atm-bg-green-300"
    },
    {
      color: "red",
      variant: "ghost",
      class: "before:!atm-bg-red-300"
    },
    {
      color: "gray",
      variant: "ghost",
      class: "before:!atm-bg-gray-400"
    },
    {
      color: "yellow",
      variant: "ghost",
      class: "before:!atm-bg-yellow-600"
    },
    {
      color: "purple",
      variant: "ghost",
      class: "before:!atm-bg-purple-600"
    }
  ]
});

// src/Badge/index.tsx
import { jsx as jsx24 } from "react/jsx-runtime";
function Badge(_a) {
  var _b = _a, {
    text,
    color = "blue",
    variant = "filled",
    showDot = true,
    className
  } = _b, props = __objRest(_b, [
    "text",
    "color",
    "variant",
    "showDot",
    "className"
  ]);
  return /* @__PURE__ */ jsx24(
    "span",
    __spreadProps(__spreadValues({
      className: StyledBadge({ className, color, variant, showDot })
    }, props), {
      children: text
    })
  );
}

// src/ImageCrop/index.tsx
import { forwardRef as forwardRef12 } from "react";
import AvatarEditor from "react-avatar-editor";
import { jsx as jsx25 } from "react/jsx-runtime";
var ImageCrop = forwardRef12(
  (_a, ref) => {
    var _b = _a, {
      image,
      zoom = 1,
      type = "circle",
      rotate = 0,
      border = 0,
      color = [0, 0, 0, 0.32]
    } = _b, rest = __objRest(_b, [
      "image",
      "zoom",
      "type",
      "rotate",
      "border",
      "color"
    ]);
    return /* @__PURE__ */ jsx25(
      AvatarEditor,
      __spreadProps(__spreadValues({}, rest), {
        ref,
        image,
        border,
        color,
        rotate,
        scale: zoom,
        borderRadius: type === "circle" ? 1e3 : 0
      })
    );
  }
);

// src/Switch/Root/index.tsx
import { forwardRef as forwardRef13 } from "react";
import { Root as PrimitiveRoot } from "@radix-ui/react-switch";

// src/Switch/Root/styles.ts
var StyledRoot3 = ie({
  base: "atm-bg-gray-300 data-[state=unchecked]disabled:atm-bg-gray-100 data-[state=checked]:disabled:atm-opacity-70 atm-rounded-full atm-group atm-relative data-[state=checked]:atm-bg-blue-200 atm-outline-none atm-cursor-pointer disabled:atm-cursor-not-allowed",
  variants: {
    size: {
      small: "atm-w-[32px] atm-h-[12px]",
      medium: "atm-w-[34px] atm-h-[14px]",
      large: "atm-w-[36px] atm-h-[18px]"
    }
  }
});

// src/Switch/Root/index.tsx
import { jsx as jsx26 } from "react/jsx-runtime";
var Root7 = forwardRef13(
  (_a, ref) => {
    var _b = _a, { children, className, size = "medium" } = _b, props = __objRest(_b, ["children", "className", "size"]);
    return /* @__PURE__ */ jsx26(
      PrimitiveRoot,
      __spreadProps(__spreadValues({}, props), {
        className: StyledRoot3({ className, size }),
        ref,
        children
      })
    );
  }
);
Root7.displayName = "Switch.Root";

// src/Switch/Thumb/index.tsx
import { forwardRef as forwardRef14 } from "react";
import {
  Thumb as PrimitiveThumb
} from "@radix-ui/react-switch";

// src/Switch/Thumb/styles.ts
var StyledThumb = ie({
  base: "atm-block atm-w-5 atm-drop-shadow-sm atm-h-5 atm-translate-x-[-1px] atm-rounded-full atm-bg-white-0 data-[state=unchecked]:group-disabled:atm-bg-gray-100  data-[state=checked]:atm-bg-blue-300 atm-transition-transform atm-duration-100 atm-will-change-transform ",
  variants: {
    size: {
      small: "atm-w-[18px] atm-h-[18px] atm-translate-y-[-3px]  data-[state=checked]:atm-translate-x-[14px]",
      medium: "atm-w-[20px] atm-h-[20px] atm-translate-y-[-3px]  data-[state=checked]:atm-translate-x-[15px]",
      large: "atm-w-[22px] atm-h-[22px] atm-translate-y-[-2px]  data-[state=checked]:atm-translate-x-[16px]"
    }
  }
});

// src/Switch/Thumb/index.tsx
import { jsx as jsx27 } from "react/jsx-runtime";
var Thumb = forwardRef14(
  (_a, ref) => {
    var _b = _a, { className, size = "medium" } = _b, props = __objRest(_b, ["className", "size"]);
    return /* @__PURE__ */ jsx27(
      PrimitiveThumb,
      __spreadProps(__spreadValues({}, props), {
        className: StyledThumb({ className, size }),
        ref
      })
    );
  }
);
Thumb.displayName = "Switch.Thumb";

// src/Switch/index.tsx
var Switch = {
  Root: Root7,
  Thumb
};

// src/Textarea/index.tsx
import { Loader2 as Loader24 } from "lucide-react";

// src/Textarea/styles.ts
var StyledLabel3 = ie({
  base: "atm-text-sm atm-font-sans atm-font-medium  atm-text-gray-600 atm-mb-1 atm-flex atm-gap-1.5"
});
var StyledLabelOptional2 = ie({
  base: "atm-text-sm atm-text-gray-300 atm-font-normal atm-font-sans"
});
var StyledTextarea = ie({
  base: "atm-flex atm-min-h-11 atm-w-full atm-px-4 atm-py-2 atm-font-sans atm-text-gray-600 atm-text-sm  atm-font-normal atm-transition-colors placeholder:atm-text-gray-300 focus-visible:atm-outline-none disabled:atm-cursor-not-allowed disabled:atm-bg-gray-50 disabled:atm-text-gray-300 disabled:atm-opacity-100 disabled:atm-border-gray-300",
  variants: {
    variant: {
      default: "atm-border atm-border-gray-200 atm-rounded-lg focus-visible:atm-border-blue-300 focus-visible:atm-ring-1 atm-ring-blue-50"
    },
    error: {
      true: "atm-border-red-300 focus-visible:atm-ring-red-100 focus-visible:atm-border-red-300"
    },
    icon: {
      true: "atm-pr-10"
    },
    startIcon: {
      true: "atm-pl-10"
    },
    resize: {
      false: "atm-resize-none"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
var StyledErrorMessage2 = ie({
  base: "atm-text-sm atm-font-sans atm-text-red-300 atm-block atm-font-normal atm-truncate atm-w-[85%]"
});
var StyledTextareaIcon = ie({
  base: "atm-h-4 atm-w-4 atm-absolute atm-top-[0.71rem] atm-right-4 atm-text-gray-400",
  variants: {
    error: {
      true: "atm-text-red-300"
    },
    loading: {
      true: "atm-animate-spinSlow"
    },
    onClickIcon: {
      true: "atm-cursor-pointer atm-transition-colors hover:atm-text-gray-500"
    }
  }
});
var StyledTextareaStartIcon = ie({
  base: "atm-h-4 atm-w-4 atm-absolute atm-top-[0.71rem] atm-left-4 atm-text-gray-400",
  variants: {
    error: {
      true: "atm-text-red-300"
    }
  }
});

// src/Textarea/index.tsx
import { forwardRef as forwardRef15 } from "react";
import { jsx as jsx28, jsxs as jsxs5 } from "react/jsx-runtime";
var Textarea = forwardRef15(
  (_a, ref) => {
    var _b = _a, {
      className,
      name,
      label,
      error,
      variant,
      required = true,
      resize = false,
      icon: Icon2,
      startIcon: StartIcon,
      loading,
      onClickIcon,
      addonBelow
    } = _b, props = __objRest(_b, [
      "className",
      "name",
      "label",
      "error",
      "variant",
      "required",
      "resize",
      "icon",
      "startIcon",
      "loading",
      "onClickIcon",
      "addonBelow"
    ]);
    const isInvalidInput = Boolean(error);
    const hasIcon = Boolean(Icon2);
    const haStartIcon = Boolean(StartIcon);
    return /* @__PURE__ */ jsxs5("div", { children: [
      Boolean(label) && /* @__PURE__ */ jsxs5(
        "label",
        {
          "data-testid": "textarea-label",
          htmlFor: name,
          className: StyledLabel3(),
          children: [
            label,
            !required && /* @__PURE__ */ jsx28(
              "span",
              {
                className: StyledLabelOptional2(),
                "data-testid": "textarea-optional-message",
                children: "(Opcional)"
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsxs5("div", { className: "atm-relative", children: [
        StartIcon && /* @__PURE__ */ jsx28(
          StartIcon,
          {
            "data-testid": "textarea-start-icon",
            className: StyledTextareaStartIcon({ error: isInvalidInput })
          }
        ),
        /* @__PURE__ */ jsx28(
          "textarea",
          __spreadValues({
            name,
            ref,
            className: StyledTextarea({
              variant,
              className: `textarea-content-scroll ${className}`,
              error: isInvalidInput,
              icon: hasIcon,
              startIcon: haStartIcon,
              resize
            }),
            "data-testid": "textarea-element"
          }, props)
        ),
        !loading && Icon2 && /* @__PURE__ */ jsx28(
          Icon2,
          {
            "data-testid": "textarea-end-icon",
            onClick: onClickIcon,
            className: StyledTextareaIcon({
              error: isInvalidInput,
              loading,
              onClickIcon: Boolean(onClickIcon)
            })
          }
        ),
        loading && /* @__PURE__ */ jsx28(
          Loader24,
          {
            "data-testid": "textarea-loading",
            className: StyledTextareaIcon({ error: isInvalidInput, loading })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs5("div", { className: "atm-flex atm-my-1 atm-items-center", children: [
        isInvalidInput && /* @__PURE__ */ jsx28(
          "p",
          {
            className: StyledErrorMessage2(),
            "data-testid": "textarea-error-message",
            children: error
          }
        ),
        addonBelow
      ] })
    ] });
  }
);

// src/Upload/index.tsx
import { Loader2 as Loader25, UploadCloud } from "lucide-react";
import { Suspense, forwardRef as forwardRef16, lazy, useRef as useRef5 } from "react";

// src/Upload/constants/index.ts
var PREVIEW_TYPE = {
  IMAGE: "image",
  ICON: "icon"
};

// src/Upload/hooks/useUploadFile.ts
import { useState as useState3 } from "react";
function useUploadFile({
  ref,
  onChange,
  onRemoveFile,
  validTypes,
  onUploadError
}) {
  const [isDragging, setIsDragging] = useState3(false);
  function handleUpload() {
    var _a;
    (_a = ref == null ? void 0 : ref.current) == null ? void 0 : _a.click();
  }
  function handleFileChange(event) {
    var _a;
    const file = (_a = event.target.files) == null ? void 0 : _a[0];
    if (!file) {
      return;
    }
    if (!(file == null ? void 0 : file.type)) {
      console.error("The file must a have type.");
      onUploadError == null ? void 0 : onUploadError("The file must a have type.");
      return;
    }
    if (!validTypes.includes(file == null ? void 0 : file.type)) {
      console.error("Invalid file type.");
      onUploadError == null ? void 0 : onUploadError("Invalid file type.");
      return;
    }
    onChange == null ? void 0 : onChange(event);
  }
  function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    onChange == null ? void 0 : onChange(event);
    setIsDragging(false);
  }
  const handleRemoveFile = () => {
    if (ref.current)
      ref.current.value = "";
    onRemoveFile == null ? void 0 : onRemoveFile();
  };
  function handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }
  function handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }
  return {
    handleDragLeave,
    isDragging,
    handleUpload,
    handleFileChange,
    handleDrop,
    handleDragOver,
    handleRemoveFile
  };
}

// src/Upload/styles.ts
var UploadContainer = ie({
  base: " atm-bg-white-0 atm-w-full hover:atm-bg-blue-50 atm-p-4 atm-border-[1px] atm-rounded-xl atm-border-dashed atm-border-blue-100 atm-flex atm-flex-col atm-justify-center atm-items-center atm-text-center atm-cursor-pointer",
  variants: {
    isDragging: {
      true: "!atm-bg-blue-50"
    }
  }
});
var IconContainer = ie({
  base: "atm-bg-blue-100 atm-rounded-full atm-flex atm-justify-center atm-items-center atm-border-solid  atm-border-blue-50"
});
var InputFile = ie({
  base: "atm-hidden"
});
var Title2 = ie({
  base: "atm-font-medium  atm-text-gray-600"
});
var Description2 = ie({
  base: "atm-text-gray-500"
});
var Preview = ie({
  base: "atm-w-full atm-h-full"
});
var DeleteIcon = ie({
  base: "atm-absolute atm-top-4 atm-right-4 atm-text-gray-400 atm-cursor-pointer"
});
var TextContainer = ie({
  base: "atm-flex atm-flex-col"
});

// src/Upload/index.tsx
import { Fragment as Fragment2, jsx as jsx29, jsxs as jsxs6 } from "react/jsx-runtime";
var ImagePreview = lazy(() => import("./ImagePreview-VFXMAL7T.mjs"));
var IconPreview = lazy(() => import("./IconPreview-GHKXVONI.mjs"));
var Upload = forwardRef16(
  (_a, ref) => {
    var _b = _a, {
      title = "Clique para fazer o upload da imagem",
      description = "PNG, JPG, GIF ou PDF (max. 10MB)",
      multiple = false,
      width,
      height,
      onChange,
      previewValue,
      previewType = "icon",
      onRemoveFile,
      iconSize = 20,
      iconGap = 4,
      iconContainerWidth = 48,
      iconContainerHeight = 48,
      iconContainerBorderWidth = 6,
      titleSize = 14,
      descriptionSize = 12,
      accept = ".png, .pdf, .jpeg, .jpg",
      validTypes = ["application/pdf", "image/jpeg", "image/png"],
      onUploadError
    } = _b, props = __objRest(_b, [
      "title",
      "description",
      "multiple",
      "width",
      "height",
      "onChange",
      "previewValue",
      "previewType",
      "onRemoveFile",
      "iconSize",
      "iconGap",
      "iconContainerWidth",
      "iconContainerHeight",
      "iconContainerBorderWidth",
      "titleSize",
      "descriptionSize",
      "accept",
      "validTypes",
      "onUploadError"
    ]);
    const inputRef = ref || useRef5(null);
    const {
      handleDragOver,
      handleDrop,
      handleFileChange,
      handleUpload,
      handleRemoveFile,
      isDragging,
      handleDragLeave
    } = useUploadFile({
      ref: inputRef,
      onChange,
      onRemoveFile,
      validTypes,
      onUploadError
    });
    return /* @__PURE__ */ jsxs6(Fragment2, { children: [
      /* @__PURE__ */ jsx29(
        "input",
        __spreadValues({
          "data-testid": "input-file",
          className: InputFile(),
          ref: inputRef,
          type: "file",
          id: "input-file-upload",
          multiple,
          accept,
          onChange: handleFileChange
        }, props)
      ),
      !previewValue && // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
      /* @__PURE__ */ jsxs6(
        "div",
        {
          "data-testid": "drop-zone",
          className: UploadContainer({ isDragging }),
          onClick: handleUpload,
          style: { width, height, gap: iconGap },
          onDragOver: handleDragOver,
          onDrop: handleDrop,
          onDragLeave: handleDragLeave,
          children: [
            /* @__PURE__ */ jsx29(
              "div",
              {
                className: IconContainer(),
                style: {
                  width: iconContainerWidth,
                  height: iconContainerHeight,
                  borderWidth: iconContainerBorderWidth
                },
                children: /* @__PURE__ */ jsx29(UploadCloud, { size: iconSize, color: colors.default[400] })
              }
            ),
            /* @__PURE__ */ jsxs6("div", { className: TextContainer(), children: [
              /* @__PURE__ */ jsx29("span", { className: Title2(), style: { fontSize: titleSize }, children: title }),
              /* @__PURE__ */ jsx29(
                "span",
                {
                  className: Description2(),
                  style: { fontSize: descriptionSize },
                  children: description
                }
              )
            ] })
          ]
        }
      ),
      /* @__PURE__ */ jsxs6(
        Suspense,
        {
          fallback: /* @__PURE__ */ jsx29(
            Loader25,
            {
              "data-testid": "spinner",
              size: 18,
              className: "atm-animate-spinSlow atm-self-center"
            }
          ),
          children: [
            previewValue && previewType === PREVIEW_TYPE.ICON && /* @__PURE__ */ jsx29(
              IconPreview,
              {
                onDelete: handleRemoveFile,
                width,
                height,
                previewValue
              }
            ),
            previewValue && previewType === PREVIEW_TYPE.IMAGE && /* @__PURE__ */ jsx29(
              ImagePreview,
              {
                previewValue,
                onEdit: handleUpload,
                onDelete: handleRemoveFile,
                onDragOver: handleDragOver,
                onDrop: handleDrop,
                onDragLeave: handleDragLeave
              }
            )
          ]
        }
      )
    ] });
  }
);

// src/Bullet/index.tsx
import { forwardRef as forwardRef17 } from "react";

// src/Bullet/styles.ts
var StyledBullet = ie({
  base: "atm-flex atm-items-center atm-bg-white atm-p-1 atm-rounded-full atm-border-gray-150 atm-border",
  variants: {
    size: {
      small: "atm-w-4 atm-h-4",
      medium: "atm-w-6 atm-h-6",
      large: "atm-w-8 atm-h-8"
    }
  }
});
var Fill = ie({
  base: "atm-w-full atm-h-full atm-rounded-full",
  variants: {
    active: {
      true: "atm-bg-blue-500",
      false: "atm-bg-white"
    }
  }
});

// src/Bullet/index.tsx
import { jsx as jsx30 } from "react/jsx-runtime";
var Bullet = forwardRef17(
  (_a, ref) => {
    var _b = _a, { active = true, size = "medium" } = _b, props = __objRest(_b, ["active", "size"]);
    return /* @__PURE__ */ jsx30(
      "div",
      __spreadProps(__spreadValues({}, props), {
        ref,
        className: StyledBullet({ size, className: props.className }),
        children: /* @__PURE__ */ jsx30("div", { className: Fill({ active }) })
      })
    );
  }
);
Bullet.displayName = "Bullet";
export {
  Alert,
  Badge,
  Bullet,
  Button2 as Button,
  Checkbox,
  Clipboard,
  DropdownMenu,
  ImageCrop,
  Input,
  InputMask,
  Radio,
  Switch,
  Textarea,
  Upload
};
